import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './pages.css';
import { cognitoForgotPassword, cognitoConfirmPassword } from '../functions/cognitoAuth';
import FormControl from '@mui/material/FormControl';
import Loader from './Loader';

export default function Verification() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const sendCode = async () => {
    if (!email) { setError('Please enter your email.'); return; }
    setLoading(true);
    setError('');
    try {
      await cognitoForgotPassword(email);
      setStep(2);
    } catch (err) {
      setError(err.message || 'Failed to send reset code.');
    }
    setLoading(false);
  };

  const confirmReset = async () => {
    if (!code || !newPassword) { setError('Please fill in both fields.'); return; }
    if (newPassword.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setLoading(true);
    setError('');
    try {
      await cognitoConfirmPassword(email, code, newPassword);
      setSuccess('Password reset successful!');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.message || 'Reset failed. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="signin-main">
      <Loader show={loading} />

      <div className="auth-left">
        <div className="auth-left-top">
          <Link to="/" className="auth-left-logo">Mermadic</Link>
          <span className="auth-left-badge">Container Platform</span>
        </div>
        <div className="auth-left-body">
          <h1 className="auth-left-headline">Reset your password</h1>
          <p className="auth-left-desc">
            We'll send a verification code to your email. Use it to set a new password.
          </p>
        </div>
      </div>

      <div className="login-container">
        <div className="login-card">
          <Link to="/" className="auth-compact-brand">
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Back to home
          </Link>
          {step === 1 ? (
            <>
              <h2 className="signin-header">Forgot password</h2>
              <p className="signin-sub">Enter your email to receive a reset code</p>
              <FormControl variant="standard" className="cnt-form">
                <div className="login-field">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email" id="email" placeholder="you@example.com"
                    value={email} onChange={e => setEmail(e.target.value)}
                  />
                </div>
                {error && <p className="signup-error">{error}</p>}
                <button className="login-submit-btn" onClick={sendCode} disabled={loading}>
                  Send code
                </button>
                <p className="login-signup-hint">
                  <Link to="/login">← Back to login</Link>
                </p>
              </FormControl>
            </>
          ) : (
            <>
              <h2 className="signin-header">Enter new password</h2>
              <p className="signin-sub">
                Check <strong>{email}</strong> for a verification code
              </p>
              <FormControl variant="standard" className="cnt-form">
                <div className="login-field">
                  <label htmlFor="code">Verification code</label>
                  <input
                    type="text" id="code" placeholder="123456" maxLength={6}
                    value={code} onChange={e => setCode(e.target.value)}
                    className="signup-code-input"
                  />
                </div>
                <div className="login-field">
                  <label htmlFor="new_password">New password</label>
                  <div className="pw-field">
                    <input
                      type={showPassword ? 'text' : 'password'} id="new_password" placeholder="Min. 8 characters"
                      value={newPassword} onChange={e => setNewPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="pw-toggle"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      aria-pressed={showPassword}
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => setShowPassword(p => !p)}
                    >
                      {showPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </div>
                {error && <p className="signup-error">{error}</p>}
                {success && <p className="signup-success">{success}</p>}
                <button className="login-submit-btn" onClick={confirmReset} disabled={loading}>
                  Reset password
                </button>
                <p className="login-signup-hint">
                  <button className="login-resend-btn" onClick={() => setStep(1)}>← Back</button>
                </p>
              </FormControl>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
